import axios from "axios";
import { getApiUrlWithVersion } from "./environmentService";
import { getAuthUser } from "./authService";
import { Segment, SegmentRules, SegmentSubscribersResponse } from "../types/segment";

class SegmentsService {
  private getAuthHeaders(): object {
    return {
      Authorization: getAuthUser()?.accessToken,
    };
  }

  async listSegments(): Promise<Segment[]> {
    const response = await axios.get(`${getApiUrlWithVersion()}/segments`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async getSegment(id: string): Promise<Segment> {
    const response = await axios.get(
      `${getApiUrlWithVersion()}/segments/${id}`,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async createSegment(name: string, rules: SegmentRules, segmentType: "static" | "dynamic" = "dynamic"): Promise<Segment> {
    const response = await axios.post(
      `${getApiUrlWithVersion()}/segments`,
      { name, rules, segment_type: segmentType },
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async updateSegment(id: string, name: string, rules: SegmentRules): Promise<Segment> {
    const response = await axios.put(
      `${getApiUrlWithVersion()}/segments/${id}`,
      { name, rules },
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async deleteSegment(id: string): Promise<void> {
    await axios.delete(`${getApiUrlWithVersion()}/segments/${id}`, {
      headers: this.getAuthHeaders(),
    });
  }

  async previewSegment(rules: SegmentRules): Promise<{ subscriber_count: number }> {
    const response = await axios.post(
      `${getApiUrlWithVersion()}/segments/preview`,
      { rules },
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async getSegmentSubscribers(id: string, page: number = 1): Promise<SegmentSubscribersResponse> {
    const response = await axios.get(
      `${getApiUrlWithVersion()}/segments/${id}/subscribers`,
      {
        headers: this.getAuthHeaders(),
        params: { page },
      }
    );
    return response.data;
  }

  async addSubscriber(segmentId: string, params: { subscriber_id?: string; email?: string }): Promise<any> {
    const response = await axios.post(
      `${getApiUrlWithVersion()}/segments/${segmentId}/subscribers`,
      params,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async removeSubscriber(segmentId: string, subscriberId: string): Promise<void> {
    await axios.delete(
      `${getApiUrlWithVersion()}/segments/${segmentId}/subscribers/${subscriberId}`,
      { headers: this.getAuthHeaders() }
    );
  }

  async refreshSegment(segmentId: string): Promise<Segment> {
    const response = await axios.post(
      `${getApiUrlWithVersion()}/segments/${segmentId}/refresh`,
      {},
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async sendCampaignToSegment(
    segmentId: string,
    newsletterEditionId: string,
    options: { sendCount?: number; sendPercentage?: number } = {}
  ): Promise<any> {
    const body: Record<string, any> = { newsletter_edition_id: newsletterEditionId };
    if (options.sendCount != null) body.send_count = options.sendCount;
    if (options.sendPercentage != null) body.send_percentage = options.sendPercentage;

    const response = await axios.post(
      `${getApiUrlWithVersion()}/segments/${segmentId}/send_campaign`,
      body,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async exportSegmentCsv(id: string): Promise<void> {
    const response = await axios.get(
      `${getApiUrlWithVersion()}/segments/${id}/export`,
      {
        headers: this.getAuthHeaders(),
        responseType: "blob",
      }
    );

    const blob = new Blob([response.data], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;

    const disposition = response.headers["content-disposition"];
    const filenameMatch = disposition?.match(/filename="?([^"]+)"?/);
    a.download = filenameMatch ? filenameMatch[1] : "segment_export.csv";

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}

const segmentsService = new SegmentsService();
export default segmentsService;
